import Button from "@ui-kit/form/Button/Button.web";
import Box from "@ui-kit/layout/Box/Box.web";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import CartPaymentMethods from "@app-contexts/public/features/basket/components/cart-summary/CartPaymentMethods/CartPaymentMethods.web";
import { useTranslation } from "@shared/utils/internationalization";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { CheckoutLabel } from "@app-contexts/public/features/detail-pages/components/shared/CheckoutLabel.web";
import { AnalyticsService } from "@shared/services/analytics";

export interface EnhancedCartSummaryFooterProps {
  onCheckout: () => void;
  showCheckoutActions?: boolean;
}

export default function CartSummaryFooter({
  onCheckout,
  showCheckoutActions = true,
}: EnhancedCartSummaryFooterProps): JSX.Element {
  const { t } = useTranslation("purchases");
  const basket = usePublicBasket();
  const { hasItems, isBasketUpdating, totalPrice, currency, supportedCCTypes } =
    basket;
  const currencyKey = currency || "CAD";
  const disableCheckout = !hasItems || isBasketUpdating;

  const handleCheckout = () => {
    AnalyticsService.buttonClick({
      label: "initiate_checkout",
      custom_params: {
        source: "cart_summary",
        item_count: basket.totalItemQuantity ?? 0,
        cart_total: totalPrice ?? 0,
      },
    });
    onCheckout();
  };

  return (
    <Box flexShrink={0} w="full">
      {hasItems && (
        <Flex
          w="full"
          justifyContent="space-between"
          alignItems="center"
          fontWeight="bold"
          mb="sm"
        >
          <Text as="span">{t("Total", { ns: "common" })}</Text>
          <Text as="span">
            <CurrencyFormat
              value={totalPrice ?? 0}
              currency={currencyKey}
              displayType="text"
              fixedDecimalScale={false}
              autoDecimalScale
            />
          </Text>
        </Flex>
      )}
      {showCheckoutActions && (
        <>
          <Button
            data-testid="enhanced-cart-summary-checkout"
            width="full"
            variant="solid"
            colorPalette="primary"
            minHeight="element.3xl"
            onClick={handleCheckout}
            isLoading={isBasketUpdating}
            disabled={disableCheckout}
          >
            <CheckoutLabel />
          </Button>
          {supportedCCTypes && supportedCCTypes.length > 0 && (
            <Box mt="sm">
              <CartPaymentMethods supportedCardTypes={supportedCCTypes} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
